import { AiOutlineException } from "react-icons/ai";
import { BiPurchaseTag, BiPurchaseTagAlt } from "react-icons/bi";
import { BsCreditCard } from "react-icons/bs";
import { FaMoneyBillAlt } from "react-icons/fa";
import { GiAchievement, GiRoad } from "react-icons/gi";
import { SiBaremetrics } from "react-icons/si";
import type { ReactNode } from "react";

/** A customer-specific dashboard link rendered in the sidebar (usually a Power BI iframe). */
export interface CustomFeature {
  name: string;
  icon: ReactNode;
  route: string;
  iframeUrl: string;
}

/** Raw per-customer overrides. Flags are optional; absent means "use the default". */
export interface CustomerConfig {
  hasFuelStopCompliance?: boolean;
  sendsFuelPlanViaWhatsApp?: boolean;
  defaultFuelLevelInGallons?: number;
  customFeatures: CustomFeature[];
}

/** The config after defaults are applied - flags are always resolved to booleans. */
export interface ResolvedCustomerConfig {
  hasFuelStopCompliance: boolean;
  sendsFuelPlanViaWhatsApp: boolean;
  defaultFuelLevelInGallons: number;
  customFeatures: CustomFeature[];
}

const dashboardConfig: Record<string, CustomerConfig> = {
  sk1: {
    customFeatures: [
      {
        name: "Fuel Prices",
        icon: <FaMoneyBillAlt className="flex-shrink-0 h-6 w-6 mr-3 text-gray-400" />,
        route: "/fuel-prices",
        iframeUrl:
          "https://app.powerbi.com/view?r=eyJrIjoiZTM2NmU1YmMtNDUwOS00ZmFmLTk4OWMtNjExMDk4NjNmYWM1IiwidCI6ImE2OTBjMThjLTcyNDYtNDhhNi1iMDUyLTYwMDk2MDg3ZjI2YiIsImMiOjN9",
      },
      {
        name: "Fuel Purchases",
        icon: <BiPurchaseTag className="flex-shrink-0 h-6 w-6 mr-3 text-gray-400" />,
        route: "/fuel-purchases",
        iframeUrl:
          "https://app.powerbi.com/view?r=eyJrIjoiZmViYjBiZWUtYzI5MS00ZDk2LThjOGEtMTQxOTJmOWQyYWIyIiwidCI6ImE2OTBjMThjLTcyNDYtNDhhNi1iMDUyLTYwMDk2MDg3ZjI2YiIsImMiOjN9&pageName=ReportSection",
      },
      {
        name: "Purchase Exceptions",
        icon: <AiOutlineException className="flex-shrink-0 h-6 w-6 mr-3 text-gray-400" />,
        route: "/purchase-exceptions",
        iframeUrl:
          "https://app.powerbi.com/view?r=eyJrIjoiZmViYjBiZWUtYzI5MS00ZDk2LThjOGEtMTQxOTJmOWQyYWIyIiwidCI6ImE2OTBjMThjLTcyNDYtNDhhNi1iMDUyLTYwMDk2MDg3ZjI2YiIsImMiOjN9&pageName=ReportSection85857afba7e6b5a06216",
      },
      {
        name: "Fuel Cards",
        icon: <BsCreditCard className="flex-shrink-0 h-6 w-6 mr-3 text-gray-400" />,
        route: "/fuel-cards",
        iframeUrl:
          "https://app.powerbi.com/view?r=eyJrIjoiNjMwN2Y0ZmYtZDYyZS00NDY5LWJjMzEtYTc0MGVlM2YzMzQzIiwidCI6ImE2OTBjMThjLTcyNDYtNDhhNi1iMDUyLTYwMDk2MDg3ZjI2YiIsImMiOjN9",
      },
      {
        name: "Miles Per Gallon",
        icon: <GiRoad className="flex-shrink-0 h-6 w-6 mr-3 text-gray-400" />,
        route: "/mpg",
        iframeUrl:
          "https://app.powerbi.com/view?r=eyJrIjoiNDMzNzgwNWQtN2ZmMy00MzNmLTlkYzctNzlkOTc1MWEzYjQ2IiwidCI6ImE2OTBjMThjLTcyNDYtNDhhNi1iMDUyLTYwMDk2MDg3ZjI2YiIsImMiOjN9",
      },
      {
        name: "Goals",
        icon: <GiAchievement className="flex-shrink-0 h-6 w-6 mr-3 text-gray-400" />,
        route: "/goals",
        iframeUrl:
          "https://app.powerbi.com/view?r=eyJrIjoiM2I2NmM0YjMtZDgwOC00OGQ2LWFkMGYtNjA0ZTk5ZjlkNTI2IiwidCI6ImE2OTBjMThjLTcyNDYtNDhhNi1iMDUyLTYwMDk2MDg3ZjI2YiIsImMiOjN9",
      },
    ],
  },
  mnz: {
    hasFuelStopCompliance: true,
    customFeatures: [
      {
        name: "Fuel Prices",
        icon: <FaMoneyBillAlt className="flex-shrink-0 h-6 w-6 mr-3 text-gray-400" />,
        route: "/fuel-prices",
        iframeUrl:
          "https://app.powerbi.com/view?r=eyJrIjoiODEzODM1MzAtZTEyMS00ZTBkLWI2MTEtMmFkZGU5NjI5ZDVjIiwidCI6ImQxYmI3MjcyLTIwMTUtNDBjYS04ZTVlLTk1YjEwZWU1ODg4YSJ9",
      },
      {
        name: "Fuel Purchases",
        icon: <BiPurchaseTag className="flex-shrink-0 h-6 w-6 mr-3 text-gray-400" />,
        route: "/fuel-purchases",
        iframeUrl:
          "https://app.powerbi.com/view?r=eyJrIjoiM2ZjNjM2MmItODVkZC00N2M3LWE1ODQtYTJmMjRiZThjMWI2IiwidCI6ImQxYmI3MjcyLTIwMTUtNDBjYS04ZTVlLTk1YjEwZWU1ODg4YSJ9",
      },
      {
        name: "Fuel Plans Metrics",
        icon: <SiBaremetrics className="flex-shrink-0 h-6 w-6 mr-3 text-gray-400" />,
        route: "/fuel-plan-metrics",
        iframeUrl:
          "https://app.powerbi.com/view?r=eyJrIjoiOGJlOTI5Y2UtMmNhYy00MjQ2LWE5OWEtZDIxM2RhN2M0YjI1IiwidCI6ImQxYmI3MjcyLTIwMTUtNDBjYS04ZTVlLTk1YjEwZWU1ODg4YSJ9",
      },
    ],
  },
  ist: {
    hasFuelStopCompliance: true,
    customFeatures: [
      {
        name: "Fuel Purchases",
        icon: <BiPurchaseTag className="flex-shrink-0 h-6 w-6 mr-3 text-gray-400" />,
        route: "/fuel-purchases",
        iframeUrl:
          "https://app.powerbi.com/view?r=eyJrIjoiOTRmYTZkNTctMWJiZC00OWRlLTkxMTktZjNhYzM3NzExMzA2IiwidCI6ImQxYmI3MjcyLTIwMTUtNDBjYS04ZTVlLTk1YjEwZWU1ODg4YSJ9",
      },
    ],
  },
  we1: {
    hasFuelStopCompliance: true,
    customFeatures: [
      {
        name: "Fuel Purchases",
        icon: <BiPurchaseTag className="flex-shrink-0 h-6 w-6 mr-3 text-gray-400" />,
        route: "/fuel-purchases",
        iframeUrl:
          "https://app.powerbi.com/view?r=eyJrIjoiNmQ5M2RmOTctNzUwYi00MTdmLTk1MmItN2UxOGI1N2RmM2FmIiwidCI6ImQxYmI3MjcyLTIwMTUtNDBjYS04ZTVlLTk1YjEwZWU1ODg4YSJ9",
      },
    ],
  },
  demo: {
    customFeatures: [],
  },
  trl: {
    defaultFuelLevelInGallons: 70,
    customFeatures: [],
  },
  gx3: {
    hasFuelStopCompliance: true,
    customFeatures: [
      // {
      //     name: "Fuel Prices",
      //     icon: <FaMoneyBillAlt className="flex-shrink-0 h-6 w-6 mr-3 text-gray-400" />,
      //     route: "/fuel-prices",
      //     iframeUrl:
      //         "https://app.powerbi.com/view?r=eyJrIjoiMzI5OGE1YzctMjdmYS00ODA3LWI2NzYtMWI5ODQzZWQ2ZjI3IiwidCI6ImQxYmI3MjcyLTIwMTUtNDBjYS04ZTVlLTk1YjEwZWU1ODg4YSJ9&pageName=ReportSection607a36c61c8fbe7592c3",
      // },
    ],
  },
  rvn: {
    hasFuelStopCompliance: true,
    customFeatures: [
      {
        name: "Fuel Plans Metrics",
        icon: <SiBaremetrics className="flex-shrink-0 h-6 w-6 mr-3 text-gray-400" />,
        route: "/fuel-plan-metrics",
        iframeUrl:
          "https://app.powerbi.com/view?r=eyJrIjoiZTVjNDM2M2UtZTVjYy00MDdlLWFiYzktM2ZhMmQ1MzNhZTJkIiwidCI6ImQxYmI3MjcyLTIwMTUtNDBjYS04ZTVlLTk1YjEwZWU1ODg4YSJ9",
      },
      {
        name: "Fuel Purchases",
        icon: <BiPurchaseTagAlt className="flex-shrink-0 h-6 w-6 mr-3 text-gray-400" />,
        route: "/fuel-purchases",
        iframeUrl:
          "https://app.powerbi.com/view?r=eyJrIjoiMjdlMjNjZDUtMGYyYi00MjliLTgzMTgtY2JkZTFjMTNlOTE0IiwidCI6ImQxYmI3MjcyLTIwMTUtNDBjYS04ZTVlLTk1YjEwZWU1ODg4YSJ9&pageName=ReportSectionbee3c452ace4d0737847",
      },
    ],
  },
  anr: {
    hasFuelStopCompliance: true,
    customFeatures: [
      {
        name: "Fuel Prices",
        icon: <FaMoneyBillAlt className="flex-shrink-0 h-6 w-6 mr-3 text-gray-400" />,
        route: "/fuel-prices",
        iframeUrl:
          "https://app.powerbi.com/view?r=eyJrIjoiNzkxYzEzMmQtM2MwYS00NjE5LWE1NmQtMmRlOWQzZTYzMDU1IiwidCI6ImQxYmI3MjcyLTIwMTUtNDBjYS04ZTVlLTk1YjEwZWU1ODg4YSJ9",
      },
      {
        name: "Fuel Purchases",
        icon: <BiPurchaseTag className="flex-shrink-0 h-6 w-6 mr-3 text-gray-400" />,
        route: "/fuel-purchases",
        iframeUrl:
          "https://app.powerbi.com/view?r=eyJrIjoiNGNlYTEwMDUtMzA1NC00N2NhLTg2NTMtNDEzZWNiZjkxZDlhIiwidCI6ImQxYmI3MjcyLTIwMTUtNDBjYS04ZTVlLTk1YjEwZWU1ODg4YSJ9",
      },
      {
        name: "Fuel Plans Metrics",
        icon: <SiBaremetrics className="flex-shrink-0 h-6 w-6 mr-3 text-gray-400" />,
        route: "/fuel-plan-metrics",
        iframeUrl:
          "https://app.powerbi.com/view?r=eyJrIjoiNGU5YjY1ODUtZDk0NS00YzU4LTlmNjYtYjdkNTgwZTdhOGUxIiwidCI6ImQxYmI3MjcyLTIwMTUtNDBjYS04ZTVlLTk1YjEwZWU1ODg4YSJ9",
      },
    ],
  },
  cls1: {
    customFeatures: [],
  },
  mlh: {
    hasFuelStopCompliance: true,
    customFeatures: [
      {
        name: "Fuel Purchases",
        icon: <BiPurchaseTag className="flex-shrink-0 h-6 w-6 mr-3 text-gray-400" />,
        route: "/fuel-purchases",
        iframeUrl:
          "https://app.powerbi.com/view?r=eyJrIjoiNzA0MjEwMjEtMjEyMC00MjdmLWIyOTktNzZhYjBjMDJkMTdkIiwidCI6ImQxYmI3MjcyLTIwMTUtNDBjYS04ZTVlLTk1YjEwZWU1ODg4YSJ9",
      },
    ],
  },
  mlg: {
    customFeatures: [],
  },
  knm: {
    customFeatures: [],
  },
  lnr: {
    customFeatures: [],
  },
  cnl: {
    hasFuelStopCompliance: true,
    customFeatures: [
      {
        name: "Fuel Prices",
        icon: <FaMoneyBillAlt className="flex-shrink-0 h-6 w-6 mr-3 text-gray-400" />,
        route: "/fuel-prices",
        iframeUrl:
          "https://app.powerbi.com/view?r=eyJrIjoiY2MzZmQ0YzMtNmUyZC00YjM0LWJlNTEtNTA0OTc2Mjg1ZGIzIiwidCI6ImQxYmI3MjcyLTIwMTUtNDBjYS04ZTVlLTk1YjEwZWU1ODg4YSJ9",
      },
      {
        name: "Fuel Purchases",
        icon: <BiPurchaseTag className="flex-shrink-0 h-6 w-6 mr-3 text-gray-400" />,
        route: "/fuel-purchases",
        iframeUrl:
          "https://app.powerbi.com/view?r=eyJrIjoiNzRkYjRkOWQtMjIwYy00YzExLWFlNTUtNGM0M2FkYzBkZmFiIiwidCI6ImQxYmI3MjcyLTIwMTUtNDBjYS04ZTVlLTk1YjEwZWU1ODg4YSJ9",
      },
      {
        name: "Fuel Plans Metrics",
        icon: <SiBaremetrics className="flex-shrink-0 h-6 w-6 mr-3 text-gray-400" />,
        route: "/fuel-plan-metrics",
        iframeUrl:
          "https://app.powerbi.com/view?r=eyJrIjoiZDkzNDU0Y2MtYTU2Ny00YTc5LWIwMWQtMWM2MzhjODFkMTU4IiwidCI6ImQxYmI3MjcyLTIwMTUtNDBjYS04ZTVlLTk1YjEwZWU1ODg4YSJ9",
      },
    ],
  },
  alt: {
    sendsFuelPlanViaWhatsApp: true,
    customFeatures: [],
  },
  pg1: {
    hasFuelStopCompliance: true,
    sendsFuelPlanViaWhatsApp: true,
    customFeatures: [
      {
        name: "Fuel Purchases",
        icon: <BiPurchaseTag className="flex-shrink-0 h-6 w-6 mr-3 text-gray-400" />,
        route: "/fuel-purchases",
        iframeUrl:
          "https://app.powerbi.com/view?r=eyJrIjoiZDYwMTY5YmQtMTc0NS00ZWZlLTk4NWYtYmZjZDIxZmFkOGVhIiwidCI6ImQxYmI3MjcyLTIwMTUtNDBjYS04ZTVlLTk1YjEwZWU1ODg4YSJ9",
      },
    ],
  },
  lfc: {
    hasFuelStopCompliance: true,
    sendsFuelPlanViaWhatsApp: true,
    customFeatures: [],
  },
  ind: {
    hasFuelStopCompliance: true,
    sendsFuelPlanViaWhatsApp: true,
    customFeatures: [],
  },
  logi: {
    sendsFuelPlanViaWhatsApp: true,
    customFeatures: [],
  },
  pgc: {
    hasFuelStopCompliance: true,
    sendsFuelPlanViaWhatsApp: true,
    customFeatures: [],
  },
  eos: {
    hasFuelStopCompliance: true,
    customFeatures: [],
  },
  zar: {
    customFeatures: [],
  },
  hnh: {
    customFeatures: [],
  },
  gei: {
    customFeatures: [],
  },
  ec1: {
    hasFuelStopCompliance: true,
    sendsFuelPlanViaWhatsApp: true,
    customFeatures: [],
  },
  rrr: {
    hasFuelStopCompliance: true,
    customFeatures: [],
  },
  tfa: {
    customFeatures: [],
  },
  sfc: {
    customFeatures: [],
  },
  pm1: {
    hasFuelStopCompliance: true,
    customFeatures: [],
  },
  ebs: {
    customFeatures: [],
  },
  est: {
    hasFuelStopCompliance: true,
    customFeatures: [],
  },
};

const defaultCustomerConfig: ResolvedCustomerConfig = {
  hasFuelStopCompliance: false,
  sendsFuelPlanViaWhatsApp: false,
  defaultFuelLevelInGallons: 15,
  customFeatures: [],
};

// Single lookup point for per-customer configuration. When this moves to the
// backend, only this function needs to change.
export function getCustomerConfig(customerCode: string): ResolvedCustomerConfig {
  return { ...defaultCustomerConfig, ...dashboardConfig[customerCode] };
}
